/**
 * Services Page - Microsoft 365 Style
 * Manage security services offered to clients
 */

import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Button,
  Input,
  Label,
  Text,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
  Badge,
} from '@fluentui/react-components';
import { EditRegular, DeleteRegular, ShieldRegular, SearchRegular } from '@fluentui/react-icons';
import { ContentCard, TableCommandBar } from '../../../components/layout';
import { SlidePanel } from '../../../components/common';
import { ApiClient, newToast } from '../../../configurations/index.config';
import { getErrorMessage } from '../../../types';

const useStyles = makeStyles({
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#323130',
    marginBottom: '4px',
    display: 'block',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#605e5c',
    display: 'block',
  },
  searchBar: {
    marginBottom: '16px',
    maxWidth: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeader: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e1dfdd',
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '13px',
    fontWeight: '600',
    color: '#323130',
  },
  tr: {
    borderBottom: '1px solid #e1dfdd',
    ':hover': {
      backgroundColor: '#f9f9f9',
    },
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#323130',
  },
  formField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '16px',
  },
  emptyState: {
    padding: '60px 20px',
    textAlign: 'center',
    color: '#605e5c',
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
  },
});

interface Service {
  id: string;
  name: string;
  description?: string;
}

export const ServicesFluentUI: React.FC = () => {
  const styles = useStyles();
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<Service | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await ApiClient.get('/get-services');
      if (response.data.status === 'success') {
        setServices(response.data.data || []);
      } else {
        newToast('error', response.data?.message || 'Failed to fetch services');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setLoading(false);
  };

  const handleOpenDialog = (service?: Service) => {
    if (service) {
      setSelectedService(service);
      setFormData({
        name: service.name,
        description: service.description || '',
      });
    } else {
      setSelectedService(null);
      setFormData({ name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      newToast('warning', 'Please enter service name');
      return;
    }

    try {
      const payload = {
        id: selectedService?.id || '',
        name: formData.name,
        description: formData.description,
      };

      const response = await ApiClient.post('/add-update-service', payload);
      if (response.data.status === 'success') {
        newToast('success', response.data.message || 'Service saved successfully');
        fetchServices();
        setDialogOpen(false);
      } else {
        newToast('error', response.data?.message || 'Failed to save service');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
  };

  const handleDelete = async () => {
    if (!selectedService) {
      return;
    }

    try {
      const response = await ApiClient.post('/delete-service', {
        id: selectedService.id,
      });

      if (response.data.status === 'success') {
        newToast('success', 'Service deleted successfully');
        fetchServices();
      } else {
        newToast('error', response.data?.message || 'Failed to delete service');
      }
    } catch (error: unknown) {
      newToast('error', getErrorMessage(error));
    }
    setDeleteDialogOpen(false);
    setSelectedService(null);
  };

  const filteredServices = services.filter(service => {
    const query = searchQuery.toLowerCase();
    return (
      service.name?.toLowerCase().includes(query) ||
      service.description?.toLowerCase().includes(query)
    );
  });

  return (
    <div>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <Text className={styles.pageTitle}>Manage Services</Text>
        <Text className={styles.pageSubtitle}>
          {loading ? 'Loading...' : `${services.length} services`}
        </Text>
      </div>

      {/* Command Bar */}
      <TableCommandBar onAdd={() => handleOpenDialog()} addLabel="Add Service" />

      {/* Search Bar */}
      <div className={styles.searchBar}>
        <Input
          placeholder="Search services..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          contentBefore={<SearchRegular />}
          size="medium"
        />
      </div>

      {/* Services Table */}
      <ContentCard noPadding>
        {loading ? (
          <div className={styles.emptyState}>
            <Text>Loading services...</Text>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className={styles.emptyState}>
            <ShieldRegular style={{ fontSize: '48px', marginBottom: '12px' }} />
            <Text size={500} weight="semibold" style={{ display: 'block' }}>
              No services found
            </Text>
            <Text style={{ marginTop: '8px', display: 'block' }}>
              {searchQuery
                ? 'Try adjusting your search'
                : 'Click "Add Service" to create your first service'}
            </Text>
          </div>
        ) : (
          <table className={styles.table}>
            <thead className={styles.tableHeader}>
              <tr>
                <th className={styles.th}>Service Name</th>
                <th className={styles.th}>Description</th>
                <th className={styles.th} style={{ width: '150px' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredServices.map(service => (
                <tr key={service.id} className={styles.tr}>
                  <td className={styles.td}>
                    <Badge appearance="filled" color="brand">
                      {service.name}
                    </Badge>
                  </td>
                  <td className={styles.td}>{service.description || '—'}</td>
                  <td className={styles.td}>
                    <div className={styles.actionButtons}>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<EditRegular />}
                        onClick={() => handleOpenDialog(service)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<DeleteRegular />}
                        onClick={() => {
                          setSelectedService(service);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ContentCard>

      {/* Add/Edit Slide Panel */}
      <SlidePanel
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title={selectedService ? 'Edit Service' : 'Add Service'}
        subtitle={selectedService ? 'Update service details' : 'Create a new service'}
        width="33%"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button appearance="primary" onClick={handleSave}>
              {selectedService ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <div className={styles.formField}>
          <Label required>Service Name</Label>
          <Input
            value={formData.name}
            onChange={e => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter service name"
          />
        </div>

        <div className={styles.formField}>
          <Label>Description</Label>
          <Textarea
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Enter description (optional)"
            rows={3}
          />
        </div>
      </SlidePanel>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(e, data) => setDeleteDialogOpen(data.open)}>
        <DialogSurface
          style={{
            maxWidth: '400px',
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          <DialogBody>
            <DialogTitle>Delete Service</DialogTitle>
            <DialogContent>
              Are you sure you want to delete <strong>{selectedService?.name}</strong>? This action
              cannot be undone.
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button appearance="primary" onClick={handleDelete}>
                Delete
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};

export default ServicesFluentUI;
